import React from 'react';
import { motion } from 'framer-motion';
import { TrendUp, TrendDown } from '@phosphor-icons/react';
import { useCurrency, CURRENCY_META } from '../context/CurrencyContext';
import { Button } from './ui/button';

const ASSET_TYPE_COLORS = {
  Stock: 'bg-blue-500/20 text-blue-400',
  ETF: 'bg-purple-500/20 text-purple-400',
  Crypto: 'bg-orange-500/20 text-orange-400',
  Commodity: 'bg-amber-500/20 text-amber-400',
  Currency: 'bg-cyan-500/20 text-cyan-400',
  India: 'bg-pink-500/20 text-pink-400',
};

const StockCard = ({ stock, onClick }) => {
  const { format: fmt, formatNative, displayCcy } = useCurrency();
  const pct = parseFloat(stock.change_percent);
  const assetType = stock.asset_type || 'Stock';

  // Price in USD (from backend price_usd or fallback to price). Display via fmt.
  const usdPrice = stock.price_usd ?? stock.price ?? 0;
  // If user is NOT in native currency view, show the native price as a small subtitle
  // (helps Indian users see ₹X for Indian stocks, US users see $X for SPCX, etc.)
  const native = stock.native_currency || 'USD';
  const showNativeHint = native !== 'USD' && native !== displayCcy && stock.price > 0;
  const usdChange = (stock.change || 0) * (stock.price_usd && stock.price ? stock.price_usd / stock.price : 1);
  // A quote can still be loading (or Yahoo rate-limited) — show a placeholder, never $0.00 / NaN%
  const hasQuote = usdPrice > 0 && Number.isFinite(pct);
  const isPositive = hasQuote ? pct >= 0 : true;

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      className="glass-card p-6 cursor-pointer group"
      onClick={onClick}
      data-testid={`stock-card-${stock.symbol}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-unbounded font-bold text-lg text-white group-hover:text-gold transition-colors truncate">
              {stock.symbol}
            </h3>
            {assetType !== 'Stock' && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${ASSET_TYPE_COLORS[assetType] || 'bg-zinc-500/20 text-zinc-400'}`} data-testid={`asset-badge-${stock.symbol}`}>
                {assetType}
              </span>
            )}
            {native !== 'USD' && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 bg-white/5 text-zinc-400 border border-white/10"
                title={`Trades natively in ${native}`}
              >
                {CURRENCY_META[native]?.flag} {native}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 line-clamp-1">{stock.company_name || stock.name}</p>
        </div>
        {hasQuote && (isPositive ? (
          <TrendUp size={24} className="text-success flex-shrink-0" weight="bold" />
        ) : (
          <TrendDown size={24} className="text-destructive flex-shrink-0" weight="bold" />
        ))}
      </div>

      <div className="space-y-1">
        <div className="flex items-end gap-2">
          <span className={`font-mono text-2xl font-bold ${hasQuote ? 'text-white' : 'text-zinc-600'}`}>
            {hasQuote ? fmt(usdPrice) : '—'}
          </span>
        </div>
        {showNativeHint && (
          <p className="text-[11px] text-zinc-500 font-mono" data-testid={`native-price-${stock.symbol}`}>
            ≈ {formatNative(stock.price, native)} native
          </p>
        )}
        
        {hasQuote ? (
          <div className="flex items-center gap-2 pt-1">
            <span
              className={`font-mono text-sm font-bold ${
                isPositive ? 'text-success' : 'text-destructive'
              }`}
            >
              {isPositive ? '+' : ''}{fmt(usdChange)}
            </span>
            <span
              className={`font-mono text-sm ${
                isPositive ? 'text-success/70' : 'text-destructive/70'
              }`}
            >
              ({isPositive ? '+' : ''}{pct.toFixed(2)}%)
            </span>
          </div>
        ) : (
          <p className="text-xs text-zinc-500 pt-1" data-testid={`price-pending-${stock.symbol}`}>
            Price updating…
          </p>
        )}
      </div>

      <Button
        className="w-full mt-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold hover:scale-105 transition-transform"
        data-testid={`trade-button-${stock.symbol}`}
      >
        Trade Now
      </Button>
    </motion.div>
  );
};

export default StockCard;